/**
 * localStorage draft for the Purchasing "New requisition" composer.
 * Survives modal close, route changes, and browser refresh (not File uploads).
 */

import type { PurchaseJobPlBucket, PurchaseOverheadGroup, PurchaseSpendClass } from '../types/purchasing';
import type { PurchaseLineDraft } from './purchasingLineItems';
import type { PurchaseLinkDraft } from './purchasingLinks';
import {
  isPurchaseJobPlBucket,
  isPurchaseOverheadGroup,
  isPurchaseSpendClass,
} from './purchasingSpendClass';

export const PURCHASE_REQ_DRAFT_VERSION = 1 as const;

export interface PurchaseRequisitionDraftV1 {
  version: typeof PURCHASE_REQ_DRAFT_VERSION;
  updatedAt: string;
  requesterUserId: string;
  vendorName: string;
  justification: string;
  spendClass: PurchaseSpendClass;
  overheadGroup: PurchaseOverheadGroup | '';
  plBucket: PurchaseJobPlBucket | '';
  jobPicker: string;
  lines: PurchaseLineDraft[];
  links: PurchaseLinkDraft[];
  paymentMethod: string;
  paymentNote: string;
}

export type PurchaseRequisitionDraft = PurchaseRequisitionDraftV1;

export function purchaseRequisitionDraftKey(userId: string): string {
  return `purchaseReqDraft:${userId || 'anon'}`;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function parseLineDraft(raw: unknown): PurchaseLineDraft | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const key = asString(row.key);
  if (!key) return null;
  const hours = Number(row.hours);
  return {
    key,
    description: asString(row.description),
    quantity: asString(row.quantity, '1') || '1',
    unitPrice: asString(row.unitPrice),
    taxable: row.taxable !== false,
    ...(Number.isFinite(hours) && hours > 0 ? { hours } : {}),
    ...(typeof row.productUrl === 'string' && row.productUrl.trim()
      ? { productUrl: row.productUrl.trim() }
      : {}),
    ...(typeof row.productLabel === 'string' && row.productLabel.trim()
      ? { productLabel: row.productLabel.trim() }
      : {}),
    ...(typeof row.vendorName === 'string' && row.vendorName.trim()
      ? { vendorName: row.vendorName.trim() }
      : {}),
  };
}

function parseLinkDraft(raw: unknown): PurchaseLinkDraft | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const key = asString(row.key);
  if (!key) return null;
  return {
    key,
    label: asString(row.label),
    url: asString(row.url),
  };
}

/** True when the draft has user-entered content worth restoring. */
export function isPurchaseRequisitionDraftMeaningful(draft: PurchaseRequisitionDraft): boolean {
  if (draft.vendorName.trim()) return true;
  if (draft.justification.trim()) return true;
  if (draft.jobPicker.trim()) return true;
  if (draft.overheadGroup) return true;
  if (draft.plBucket) return true;
  if (draft.spendClass !== 'overhead') return true;
  if (draft.paymentNote.trim()) return true;
  if (draft.links.some(l => l.label.trim() || l.url.trim())) return true;
  return draft.lines.some(
    l =>
      l.description.trim() ||
      l.unitPrice.trim() ||
      (l.quantity.trim() !== '' && l.quantity.trim() !== '1') ||
      !!l.productUrl?.trim() ||
      !!l.productLabel?.trim() ||
      !!l.vendorName?.trim(),
  );
}

export function parsePurchaseRequisitionDraft(raw: unknown): PurchaseRequisitionDraft | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  if (row.version !== PURCHASE_REQ_DRAFT_VERSION) return null;
  const spendClass = isPurchaseSpendClass(row.spendClass) ? row.spendClass : 'overhead';
  const overheadRaw = row.overheadGroup;
  const overheadGroup =
    overheadRaw === '' || overheadRaw == null
      ? ''
      : isPurchaseOverheadGroup(overheadRaw)
        ? overheadRaw
        : '';
  const plRaw = row.plBucket;
  const plBucket =
    plRaw === '' || plRaw == null ? '' : isPurchaseJobPlBucket(plRaw) ? plRaw : '';
  const lines = Array.isArray(row.lines)
    ? row.lines.map(parseLineDraft).filter((l): l is PurchaseLineDraft => !!l)
    : [];
  const links = Array.isArray(row.links)
    ? row.links.map(parseLinkDraft).filter((l): l is PurchaseLinkDraft => !!l)
    : [];
  return {
    version: PURCHASE_REQ_DRAFT_VERSION,
    updatedAt: asString(row.updatedAt, new Date().toISOString()),
    requesterUserId: asString(row.requesterUserId),
    vendorName: asString(row.vendorName),
    justification: asString(row.justification),
    spendClass,
    overheadGroup,
    plBucket,
    jobPicker: asString(row.jobPicker),
    lines: lines.length ? lines : [],
    links,
    paymentMethod: asString(row.paymentMethod),
    paymentNote: asString(row.paymentNote),
  };
}

export function loadPurchaseRequisitionDraft(userId: string): PurchaseRequisitionDraft | null {
  if (typeof window === 'undefined' || !userId) return null;
  try {
    const saved = window.localStorage.getItem(purchaseRequisitionDraftKey(userId));
    if (!saved) return null;
    const parsed = parsePurchaseRequisitionDraft(JSON.parse(saved) as unknown);
    if (!parsed || !isPurchaseRequisitionDraftMeaningful(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function savePurchaseRequisitionDraft(
  userId: string,
  draft: Omit<PurchaseRequisitionDraft, 'version' | 'updatedAt'>,
): void {
  if (typeof window === 'undefined' || !userId) return;
  const payload: PurchaseRequisitionDraft = {
    ...draft,
    version: PURCHASE_REQ_DRAFT_VERSION,
    updatedAt: new Date().toISOString(),
  };
  try {
    if (!isPurchaseRequisitionDraftMeaningful(payload)) {
      window.localStorage.removeItem(purchaseRequisitionDraftKey(userId));
      return;
    }
    window.localStorage.setItem(purchaseRequisitionDraftKey(userId), JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
}

export function clearPurchaseRequisitionDraft(userId: string): void {
  if (typeof window === 'undefined' || !userId) return;
  try {
    window.localStorage.removeItem(purchaseRequisitionDraftKey(userId));
  } catch {
    /* ignore */
  }
}

export function hasPurchaseRequisitionDraft(userId: string): boolean {
  return !!loadPurchaseRequisitionDraft(userId);
}
